import Button from '@cube3/ui/src/Buttons/Button';
import { Amount } from '@cube3/ui/src/human';
import Surface from '@cube3/ui/src/Surface/Surface';
import { Typography } from '@cube3/ui/src/typography/Typography';
import { VirtualScrolling } from '@cube3/ui/src/VirtualPages/next/VirtualScrolling';
import { LinearProgress, Tooltip } from '@material-ui/core';
import React from 'react';
import { Padding } from '../../Contracts/prefabs/Padding';
import { useUploadQueueController } from '../hooks/useUploadQueueController';
import { UploadQueueListItem } from './UploadQueueListItem';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import ModuleLoader from '@cube3/ui/src/Prefabs/Loaders/ModuleLoader';
import { Shimmer } from '@cube3/cubicle/src/core/atoms/Loader/Shimmer';

interface Props {
  //...
}

export const UploadQueue: React.FunctionComponent<Props> = React.memo(
  (props) => {
    const {
      fresh,
      sortedUploads,
      inactive,
      failed,
      handleClearFinished,
      handleClearFailed,
      items,
      focusItem,
      setFocusItem,
      pageSize,
      setPageSize
    } = useUploadQueueController();

    const busy = useTypedSelector((state) => state.ui.uploadQueueBusy);
    const percentage = useTypedSelector((state) => state.ui.uploadQueueFolderPercentage);

    if (busy) {
      return (
        <>
          <ModuleLoader message={`Preparing folder upload... (${percentage}%)`} percentage={percentage} />
        </>
      );
    }

    if (!fresh) {
      return (
        <>
          <Shimmer height={140} width="100%" fadeIn={true} margin="medium" />
          <Shimmer height={140} width="100%" fadeIn={true} margin="medium" />
          <Shimmer height={140} width="100%" fadeIn={true} margin="medium" />
        </>
      );
    }

    if (!sortedUploads.length) {
      return (
        <Padding>
          <Typography
            color="contrast2"
            typographyProps={{ gutterBottom: true }}
          >
            {"No uploads since you've last signed in"}
          </Typography>
          <Typography typographyStyle="body3" color="contrast3">
            Uploads are cleared automatically after 7 days
          </Typography>
        </Padding>
      );
    }

    // make room for notifications
    let offset = 0;
    if (inactive.length > 0) {
      offset += 40;
    }
    if (failed.length > 0) {
      offset += 40;
    }

    return (
      <>
        {inactive.length > 0 && (
          <Surface background="base2" gradient="lamp" overlay="dark">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 8px'
              }}
            >
              <Typography>
                <Amount count={inactive.length} word="asset" /> uploaded
                successfully
              </Typography>
              <Tooltip title="Remove items that have finished uploading">
                <span>
                  <Button
                    onClick={handleClearFinished}
                    size="small"
                    colorVariant="ghost1"
                  >
                    Clean up
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Surface>
        )}{' '}
        {failed.length > 0 && (
          <Surface background="base2" gradient="lamp" overlay="dark">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 8px'
              }}
            >
              <Typography color="danger1">
                <Amount count={failed.length} word="asset" /> failed to upload
                or process
              </Typography>
              <Tooltip title="Remove items that have failed uploading">
                <span>
                  <Button
                    onClick={handleClearFailed}
                    size="small"
                    colorVariant="danger"
                  >
                    Remove
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Surface>
        )}
        <VirtualScrolling
          height={`calc(100% - ${offset}px)`}
          items={items}
          itemHeight={140}
          // renderItem={renderItem}
          totalItems={sortedUploads.length}
          focusItem={focusItem}
          onFocusItem={setFocusItem}
          onPageSize={setPageSize}
          pageSize={pageSize}
          itemComponent={RenderItem}
        />
      </>
    );
  }
);

const RenderItem = ({ item }) => {
  return <UploadQueueListItem uploadId={item.id} />;
};
